// src/views/letters/LettersView.tsx
// Literuj dla grupy Regular — kopia src/views/kiddo-games/letters/LettersView.tsx (gra z Easy), rozwijana osobno.
// Różnice: własne style i komponenty.
import React, {
  memo, useEffect, useState, useCallback, useRef,
  useMemo,
} from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button, Spin, Result } from 'antd';
import classes from './LettersView.module.less';
import FixWidthContent from '../../components/FixWidthContent';
import withTagManager from '../../hooks/withTagManager';
import InfoButton from '../../components/InfoButton/InfoButton';
import RulesOfTheGame from './RulesOfTheGame';
import { getActiveList, getUserData } from '../auth/slice/selectors';
import { BASE_API_URL } from '../../services/ApiService';
import { notifyAudioError } from '../../utils/audioErrors';
import useGoToRoute from '../../hooks/useGoToRoute';
import { Path } from '../../constants/Path';
import EndGameInfo from '../../components/EndGameInfo/EndgameInfo';
import { RightSideButtons } from '../../components/RightSideButtons/RightSideButtons';
import { UnsplashCredits } from '../../components/UnsplashCredits/UnsplashCredits';
import { EditTermButton } from '../../components/EditTermModal/EditTermButton';
import { Roles } from '../../constants/Roles';
import { LetterButton } from './components/LetterButton';
import { LetterSlot } from './components/LetterSlot';
import { ImageChoice } from './components/ImageChoice';
import { FinalSummary } from './components/FinalSummary';
import { ReactComponent as Sound } from '../../assets/icons/games/find/volume-high.svg';
import { ReactComponent as Skip } from '../../assets/icons/games/writing/arrow-right-rec.svg';
import { ReactComponent as ThumbUp } from '../../assets/icons/games/writing/thumbs-up.svg';
import { cleanTextForTTS } from '../../utils/textBracketSanitize';
import WordCounter from '../../components/WordCounter';
import { EditListButton } from '../../components/EditListButton/EditListButton';
import useActivityTracking from '../../hooks/useActivityTracking';
import { ActivityGame, recordAnswer } from '../../services/activity';

enum GameStage {
  BUILDING_WORD = 'building',
  CHOOSING_IMAGE = 'choosing',
  FINAL_SUMMARY = 'summary',
  GAME_END = 'end',
}

interface LetterState {
  letter: string;
  originalIndex: number;
  targetSlotIndex: number | null;
  isPlaced: boolean;
  isWrong: boolean;
}

interface ImageOption {
  id: string | number;
  image: string;
  term: string;
  isCorrect: boolean;
  isWrong: boolean;
  isSelected?: boolean;
}

export const LettersView = memo(() => {
  const { t } = useTranslation();
  const activeList = useSelector(getActiveList);
  const goToHome = useGoToRoute(Path.HOME);

  const hasValidData = activeList && activeList.words && activeList.words.length > 0;

  const user = useSelector(getUserData);
  const canEditTerms = user?.role === Roles.TEACHER;

  // Próbą jest wybór obrazka po ułożeniu słowa albo SKIP; pojedyncze litery próbami nie są
  useActivityTracking(ActivityGame.LETTERS);

  const [currentWords, setCurrentWords] = useState(() => {
    if (!hasValidData) return [];
    const wordsCopy = [...activeList.words];

    return wordsCopy.sort(() => 0.5 - Math.random());
  });

  const [wordIndex, setWordIndex] = useState<number>(0);
  const [gameStage, setGameStage] = useState<GameStage>(GameStage.BUILDING_WORD);
  const [isLoading, setIsLoading] = useState(true);
  const [letters, setLetters] = useState<LetterState[]>([]);
  const [currentLetterIndex, setCurrentLetterIndex] = useState<number>(0);
  const [imageOptions, setImageOptions] = useState<ImageOption[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showThumbUp, setShowThumbUp] = useState(false);
  const [showWrongAnswerHint, setShowWrongAnswerHint] = useState(false);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const wrongAudioTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const letterAudioTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const thumbUpTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const wrongAnswerHintTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const mountingIdRef = useRef(0);

  const currentWord = currentWords[wordIndex];
  const rawTerm = currentWord?.term || '';
  const targetWord = cleanTextForTTS(rawTerm);
  // const targetLetters = targetWord.split('');
  const targetLetters = useMemo(() => targetWord.split(''), [targetWord]);

  // 🔧 CLEANUP
  const cleanupAllResources = useCallback(() => {
    mountingIdRef.current += 1;

    if (audioRef.current) {
      try {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current.src = '';
      } catch (e) {
        // Ignoruj
      }
      audioRef.current = null;
    }

    if (wrongAudioTimeoutRef.current) {
      clearTimeout(wrongAudioTimeoutRef.current);
      wrongAudioTimeoutRef.current = null;
    }
    if (letterAudioTimeoutRef.current) {
      clearTimeout(letterAudioTimeoutRef.current);
      letterAudioTimeoutRef.current = null;
    }
    if (thumbUpTimeoutRef.current) {
      clearTimeout(thumbUpTimeoutRef.current);
      thumbUpTimeoutRef.current = null;
    }
    if (wrongAnswerHintTimeoutRef.current) {
      clearTimeout(wrongAnswerHintTimeoutRef.current);
      wrongAnswerHintTimeoutRef.current = null;
    }

    setIsProcessing(false);
    setShowThumbUp(false);
    setShowWrongAnswerHint(false);
  }, []);

  // 🔧 PLAY AUDIO
  const playAudio = useCallback((text: string, skipCleanup = false) => {
    if (!skipCleanup) {
      if (audioRef.current) {
        try {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
          audioRef.current.src = '';
        } catch (e) {
          // Ignoruj
        }
        audioRef.current = null;
      }
    }

    const encodedText = encodeURIComponent(text.toUpperCase());
    const audioUrl = `${BASE_API_URL}/v1/tts?q=${encodedText}`;
    const audio = new Audio(audioUrl);
    audioRef.current = audio;

    const playPromise = audio.play();
    if (playPromise !== undefined) {
      playPromise.catch((error) => {
        if (error.name !== 'AbortError' && error.name !== 'NotAllowedError') {
          console.error('Error playing audio:', error);
          notifyAudioError(error);
        }
      });
    }
  }, []);

  const skipSpacesAndPlayNext = useCallback((startIndex: number) => {
    const currentMountingId = mountingIdRef.current;
    let nextIndex = startIndex;

    while (nextIndex < targetLetters.length && targetLetters[nextIndex] === ' ') {
      nextIndex += 1;
    }

    setCurrentLetterIndex(nextIndex);

    if (nextIndex < targetLetters.length) {
      letterAudioTimeoutRef.current = setTimeout(() => {
        if (currentMountingId !== mountingIdRef.current) return;
        playAudio(targetLetters[nextIndex]);
      }, 800);
    }

    return nextIndex;
  }, [targetLetters, playAudio]);

  const initializeLetters = useCallback(() => {
    if (!targetWord) return;

    cleanupAllResources();

    const letterArray: LetterState[] = targetWord
      .split('')
      .map((letter: string, index: number) => ({
        letter: letter.toUpperCase(),
        originalIndex: index,
        targetSlotIndex: null,
        isPlaced: letter === ' ',
        isWrong: false,
      }))
      .filter((item) => item.letter !== ' ');

    const shuffled = [...letterArray].sort(() => 0.5 - Math.random());
    setLetters(shuffled);

    const firstNonSpaceIndex = targetWord.split('').findIndex((l) => l !== ' ');
    setCurrentLetterIndex(firstNonSpaceIndex >= 0 ? firstNonSpaceIndex : 0);
  }, [targetWord, cleanupAllResources]);

  // Inicjalizacja gry
  useEffect(() => {
    setIsLoading(false);
    if (hasValidData && currentWord) {
      initializeLetters();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasValidData, currentWord?.id]);

  // Automatyczne audio na początku etapu BUILDING_WORD
  useEffect(() => {
    if (gameStage === GameStage.BUILDING_WORD && targetLetters.length > 0) {
      const firstNonSpaceIndex = targetLetters.findIndex((l) => l !== ' ');
      if (firstNonSpaceIndex >= 0) {
        const timeoutId = setTimeout(() => {
          playAudio(targetLetters[firstNonSpaceIndex]);
        }, 300);

        return () => clearTimeout(timeoutId);
      }
    }

    return undefined;
  }, [gameStage, wordIndex, targetLetters, playAudio]);

  useEffect(() => {
    cleanupAllResources();
  }, [wordIndex, gameStage, cleanupAllResources]);

  // Przygotuj opcje obrazków
  const prepareImageOptions = useCallback(() => {
    if (!currentWord || !activeList.words) return;

    const correctOption: ImageOption = {
      id: currentWord.id,
      image: currentWord.image,
      term: currentWord.term,
      isCorrect: true,
      isWrong: false,
    };

    const otherWords = activeList.words.filter((w: { id: any }) => w.id !== currentWord.id);
    const shuffledOthers = [...otherWords].sort(() => 0.5 - Math.random());
    const wrongOptions: ImageOption[] = shuffledOthers.slice(0, 2).map((w) => ({
      id: w.id,
      image: w.image,
      term: w.term,
      isCorrect: false,
      isWrong: false,
    }));

    const allOptions = [correctOption, ...wrongOptions].sort(() => 0.5 - Math.random());
    setImageOptions(allOptions);
  }, [currentWord, activeList]);

  // Handler przycisku SOUND
  const handleSoundClick = useCallback(() => {
    if (currentLetterIndex < targetLetters.length && targetLetters[currentLetterIndex] !== ' ') {
      playAudio(targetLetters[currentLetterIndex]);
    }
  }, [targetLetters, currentLetterIndex, playAudio]);

  const handleLetterClick = useCallback((clickedLetter: LetterState, event: React.MouseEvent<HTMLButtonElement>) => {
    if (isProcessing || clickedLetter.isPlaced) return;

    const expectedLetter = targetLetters[currentLetterIndex];
    const currentMountingId = mountingIdRef.current;

    // Usuń focus z przycisku
    event.currentTarget.blur();

    if (clickedLetter.letter.toUpperCase() === expectedLetter.toUpperCase()) {
    // ✅ PRAWIDŁOWA LITERA
      setLetters((prev) => prev.map((l) => (l === clickedLetter
        ? {
          ...l, isPlaced: true, isWrong: false, targetSlotIndex: currentLetterIndex,
        }
        : l)));

      if (currentLetterIndex < targetLetters.length - 1) {
        const nextIndex = skipSpacesAndPlayNext(currentLetterIndex + 1);

        if (nextIndex >= targetLetters.length) {
          setShowThumbUp(true);
          thumbUpTimeoutRef.current = setTimeout(() => {
            if (currentMountingId !== mountingIdRef.current) return;
            setShowThumbUp(false);
            setGameStage(GameStage.CHOOSING_IMAGE);
            prepareImageOptions();
          }, 1500);
        }
      } else {
      // Wszystkie litery ułożone
        setShowThumbUp(true);
        thumbUpTimeoutRef.current = setTimeout(() => {
          if (currentMountingId !== mountingIdRef.current) return;
          setShowThumbUp(false);
          setGameStage(GameStage.CHOOSING_IMAGE);
          prepareImageOptions();
        }, 1500);
      }
    } else {
      // ❌ ZŁA LITERA
      // Wyczyść zaległe audio timeouty
      if (letterAudioTimeoutRef.current) {
        clearTimeout(letterAudioTimeoutRef.current);
        letterAudioTimeoutRef.current = null;
      }
      if (wrongAudioTimeoutRef.current) {
        clearTimeout(wrongAudioTimeoutRef.current);
        wrongAudioTimeoutRef.current = null;
      }

      setIsProcessing(true);
      setLetters((prev) => prev.map((l) => (l === clickedLetter ? { ...l, isWrong: true } : l)));

      wrongAudioTimeoutRef.current = setTimeout(() => {
        if (currentMountingId !== mountingIdRef.current) return;
        if (gameStage !== GameStage.BUILDING_WORD) return;

        setLetters((prev) => prev.map((l) => ({ ...l, isWrong: false })));
        setIsProcessing(false);

        if (
          currentLetterIndex < targetLetters.length
      && targetLetters[currentLetterIndex] !== ' '
        ) {
          playAudio(targetLetters[currentLetterIndex]);
        }
      }, 1200);
    }
  }, [
    isProcessing,
    targetLetters,
    currentLetterIndex,
    gameStage,
    playAudio,
    prepareImageOptions,
    skipSpacesAndPlayNext,
  ]);

  // Kliknięcie w obrazek
  const handleImageClick = useCallback(
    (option: ImageOption) => {
      if (isProcessing) return;

      recordAnswer(option.isCorrect, currentWord?.term);

      const currentMountingId = mountingIdRef.current;

      if (option.isCorrect) {
        setIsProcessing(true);
        // Oznacz poprawny wybór jako "selected"
        setImageOptions((prev) => prev.map((opt) => (opt.id === option.id
          ? { ...opt, isSelected: true, isWrong: false }
          : { ...opt, isWrong: false })));
        setShowWrongAnswerHint(false);

        setTimeout(() => {
          if (currentMountingId !== mountingIdRef.current) return;
          setGameStage(GameStage.FINAL_SUMMARY);
          setIsProcessing(false);
        }, 800);
      } else {
        setIsProcessing(true);
        setImageOptions((prev) => prev.map((opt) => (opt.id === option.id ? { ...opt, isWrong: true } : opt)));

        // Pokaż napis zamiast audio
        setShowWrongAnswerHint(true);

        wrongAnswerHintTimeoutRef.current = setTimeout(() => {
          if (currentMountingId !== mountingIdRef.current) return;
          setShowWrongAnswerHint(false);
          setImageOptions((prev) => prev.map((opt) => ({ ...opt, isWrong: false })));
          setIsProcessing(false);
        }, 3000);
      }
    },
    [isProcessing, currentWord],
  );

  // NEXT
  const handleNextWord = useCallback(() => {
    cleanupAllResources();

    if (wordIndex < currentWords.length - 1) {
      setWordIndex((prev) => prev + 1);
      setGameStage(GameStage.BUILDING_WORD);
      setImageOptions([]);
    } else {
      setGameStage(GameStage.GAME_END);
    }
  }, [wordIndex, currentWords.length, cleanupAllResources]);

  // SKIP — w monitoringu aktywności próba z wynikiem `false`. NEXT na podsumowaniu próbą nie jest. Nie jest nią też SKIP
  // kliknięty po dobrym obrazku, zanim wejdzie podsumowanie — ta próba jest już zapisana jako dobra
  const handleSkip = useCallback(() => {
    if (!imageOptions.some((option) => option.isSelected)) {
      recordAnswer(false, currentWord?.term);
    }
    handleNextWord();
  }, [imageOptions, currentWord, handleNextWord]);

  // REPEAT
  const repeatGame = useCallback(() => {
    cleanupAllResources();

    const wordsCopy = [...activeList.words];
    setCurrentWords(wordsCopy.sort(() => 0.5 - Math.random()));
    setWordIndex(0);
    setGameStage(GameStage.BUILDING_WORD);
    setImageOptions([]);
  }, [activeList, cleanupAllResources]);

  // Cleanup przy unmount
  useEffect(
    () => () => {
      cleanupAllResources();
    },
    [cleanupAllResources],
  );

  if (isLoading) {
    return (
      <FixWidthContent>
        <div className={classes.lettersLoading}>
          <Spin size="large" tip={t('games:states.loadingList')} />
        </div>
      </FixWidthContent>
    );
  }

  if (!hasValidData || currentWords.length === 0) {
    return (
      <FixWidthContent>
        <Result
          status="warning"
          title={t('games:states.noActiveList')}
          subTitle={t('games:states.chooseList')}
          extra={(
            <Button type="primary" onClick={goToHome}>
              {t('games:states.backToLists')}
            </Button>
          )}
        />
      </FixWidthContent>
    );
  }

  // Podpis dotyczy tylko zdjęć widocznych na danym etapie: na etapie wyboru są to opcje
  // (ich `id` to `id` słowa), na podsumowaniu — zdjęcie bieżącego słowa
  const creditWords = gameStage === GameStage.CHOOSING_IMAGE
    ? imageOptions.map((option) => (activeList?.words || []).find((w: any) => w.id === option.id))
    : [currentWord];

  return (
    <>
      <FixWidthContent className={classes.lettersMain}>
        {gameStage !== GameStage.GAME_END && (
          <WordCounter
            current={wordIndex + 1}
            total={currentWords.length}
          />
        )}
        {gameStage === GameStage.BUILDING_WORD && (
          <div className={classes.buildingStage}>
            <div className={classes.letterSlots}>
              {targetLetters.map((letter: string, index: number) => {
                const isSpace = letter === ' ';
                const placedLetter = letters.find(
                  (l) => l.targetSlotIndex === index && l.isPlaced,
                );
                const slotKey = `${currentWord.id}-slot-${index}`;

                return (
                  <LetterSlot
                    key={slotKey}
                    letter={placedLetter ? placedLetter.letter : ''}
                    isActive={index === currentLetterIndex}
                    isSpace={isSpace}
                  />
                );
              })}
            </div>

            <div className={classes.letterButtons}>
              {letters.map((letterState) => (
                <LetterButton
                  key={`btn-${letterState.originalIndex}-${letterState.letter}`}
                  letter={letterState.letter}
                  isPlaced={letterState.isPlaced}
                  isWrong={letterState.isWrong}
                  onClick={(e) => handleLetterClick(letterState, e)}
                />
              ))}
            </div>

            <div className={classes.soundButtonContainer}>
              <Button id={classes.soundButton} onClick={handleSoundClick}>
                <Sound className={classes.btnLabel} />
                <span className={classes.btnText}>{t('games:buttons.sound')}</span>
              </Button>
            </div>

            {/* Kciuk jak w pozostałych grach Regular, pod SOUND jak komunikat w Napisz; Literuj Easy ma zostać przy emoji.
                Miejsce zajęte zawsze, żeby SKIP nie skakał, gdy kciuk się pojawia */}
            <div className={classes.thumbUpContainer}>
              {showThumbUp && <ThumbUp className={classes.thumbUp} />}
            </div>
          </div>
        )}

        {gameStage === GameStage.CHOOSING_IMAGE && (
          <div className={classes.choosingStage}>
            <div className={classes.instructionContainer}>
              <div className={classes.instruction}>{t('games:choosePicture')}</div>
              {/* Słowo po błędzie stoi zawsze, ale widać je dopiero po błędzie — od początku zajmuje swoje miejsce, więc gdy
                  się pojawi, ani obrazki, ani polecenie się nie przesuwają (LettersView.module.less) */}
              <div className={`${classes.wrongAnswerHint} ${showWrongAnswerHint ? classes.wrongAnswerHintShown : ''}`}>
                {targetWord}
              </div>
            </div>
            <div className={classes.imageOptions}>
              {imageOptions.map((option) => (
                <ImageChoice
                  key={option.id}
                  image={option.image}
                  isSelected={option.isSelected}
                  isWrong={option.isWrong}
                  onClick={() => handleImageClick(option)}
                />
              ))}
            </div>
          </div>
        )}

        {/* SKIP przy układaniu liter i wyborze obrazka; na podsumowaniu jest już NEXT. Stoi na dole ekranu,
            na środku, żeby nie zachęcał do pomijania słów — jak w Literuj Easy */}
        {(gameStage === GameStage.BUILDING_WORD || gameStage === GameStage.CHOOSING_IMAGE) && (
          <div className={classes.skipContainer}>
            <Button
              className={classes.skipButton}
              onClick={handleSkip}
              aria-label={t('games:labels.skipWord')}
            >
              <Skip className={classes.btnLabel} />
              <br />
              {t('games:buttons.skip')}
            </Button>
          </div>
        )}

        {gameStage === GameStage.FINAL_SUMMARY && (
          <FinalSummary word={targetWord} image={currentWord.image} onNext={handleNextWord} />
        )}

        {/* Literuj stoi poza kolejką gier: bez następnej gry i bez „the end” — zakończenie całości jest po Napisz */}
        {gameStage === GameStage.GAME_END && (
          <EndGameInfo repeatGameTrigger={repeatGame} />
        )}

        {gameStage !== GameStage.GAME_END && <UnsplashCredits words={creditWords} />}

        <RightSideButtons>
          <InfoButton content={<RulesOfTheGame />} />

          {canEditTerms && currentWord && gameStage !== GameStage.GAME_END && (
            <EditTermButton
              wordId={currentWord.id}
              currentTerm={currentWord.term}
              currentDefinition={currentWord.definition}
              listId={activeList?.id || ''}
              onTermUpdated={(newTerm, newDefinition) => {
                setCurrentWords((prev) => prev.map((word) => (word.id === currentWord.id
                  ? { ...word, term: newTerm, definition: newDefinition }
                  : word)));
              }}
            />
          )}

          {canEditTerms && activeList?.id && (
            <EditListButton listId={activeList.id} />
          )}

        </RightSideButtons>

      </FixWidthContent>
    </>
  );
});

export default withTagManager(LettersView);
